import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, FileText, Users, Plus, Loader2, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { OnboardingNote } from '../../../api/onboardingApi';
import * as api from '../../../api/onboardingApi';

const noteTypeConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  meeting: { label: 'Meeting', icon: <Users className="h-4 w-4" />, color: 'bg-blue-100 text-blue-700' },
  internal: { label: 'Internal', icon: <FileText className="h-4 w-4" />, color: 'bg-gray-100 text-gray-700' },
  customer_facing: { label: 'Customer', icon: <MessageSquare className="h-4 w-4" />, color: 'bg-green-100 text-green-700' },
};

interface Props { planId: number; }

export default function NotesTimeline({ planId }: Props) {
  const { t } = useAppTranslation();
  const [notes, setNotes] = useState<OnboardingNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ note_type: 'internal', title: '', body: '', meeting_date: '' });

  const fetchNotes = useCallback(async () => {
    try {
      setNotes(await api.listNotes(planId));
    } catch (err) { console.error('Error fetching notes:', err); }
    finally { setLoading(false); }
  }, [planId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleSave = async () => {
    if (!form.body.trim()) return;
    setSaving(true);
    try {
      await api.addNote(planId, {
        note_type: form.note_type,
        title: form.title || undefined,
        body: form.body,
        meeting_date: form.meeting_date || undefined,
      });
      setShowAdd(false);
      setForm({ note_type: 'internal', title: '', body: '', meeting_date: '' });
      fetchNotes();
    } catch (err: any) {
      alert('Failed to save note: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-sm">{t('onboarding.notes.title', 'Notes & Meetings')}</h3>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}><Plus className="h-3 w-3 mr-1" />Add Note</Button>
      </div>

      {notes.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No notes yet</p>
      ) : (
        <div className="relative border-l-2 border-gray-200 ml-3 space-y-4">
          {notes.map((n) => {
            const cfg = noteTypeConfig[n.note_type] || noteTypeConfig.internal;
            return (
              <div key={n.id} className="ml-6 relative">
                <div className="absolute -left-[33px] top-1 w-4 h-4 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                </div>
                <Card className="shadow-sm">
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className={cfg.color}>{cfg.icon}<span className="ml-1">{cfg.label}</span></Badge>
                      <span className="text-xs text-gray-400">
                        {n.meeting_date ? new Date(n.meeting_date).toLocaleDateString() : new Date(n.created_at).toLocaleDateString()}
                      </span>
                      {n.author_name && <span className="text-xs text-gray-500">by {n.author_name}</span>}
                    </div>
                    {n.title && <p className="font-medium text-sm">{n.title}</p>}
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{n.body}</p>
                    {n.attendees && n.attendees.length > 0 && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                        <Users className="h-3 w-3" />
                        {n.attendees.map((a: any, i: number) => <span key={i}>{a.name || a.email}{i < n.attendees!.length - 1 ? ', ' : ''}</span>)}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Type</Label>
              <Select value={form.note_type} onValueChange={(v) => setForm({ ...form, note_type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Note</SelectItem>
                  <SelectItem value="meeting">Meeting Note</SelectItem>
                  <SelectItem value="customer_facing">Customer-Facing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.note_type === 'meeting' && (
              <div>
                <Label>Meeting Date</Label>
                <Input type="date" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} className="mt-1" />
              </div>
            )}
            <div>
              <Label>Title (optional)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="e.g., Weekly sync" />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1" placeholder="Write your note..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.body.trim()}>
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</> : 'Save Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
